import React, { Suspense, useEffect, useMemo, useRef } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { hasDaxAnimationFeatureFlag } from '../../molecules/URLFeatureFlag/URLFeatureFlag'

// Local copy of the exported Blender model (textures/materials embedded). For
// studio-app/test deploys this is served from the build's /assets path; later
// it can move to the CDN via getModelUrl() (see src/utils/animations.ts).
const ASSET_BASE = `${process.env.PUBLIC_URL ?? ''}/assets/animations/dax`
const MODEL_URL = `${ASSET_BASE}/dax.glb`

// Only pre-fetch the (large) GLB when Dax is actually enabled. This module is
// imported by consumers for its runtime constants (DAX_CANVAS_FILL, DAX_CLIP_NAMES)
// even off-flag, so guard the download so non-flagged accounts never fetch the asset.
if (hasDaxAnimationFeatureFlag()) useGLTF.preload(MODEL_URL)

// Camera + fit constants. The model is scaled so its height is TARGET_HEIGHT world
// units, viewed by a camera whose visible frustum at the model is FRUSTUM_HEIGHT units
// tall (2 * distance * tan(fov/2)). Dax's body therefore fills DAX_CANVAS_FILL of the
// canvas height. Callers size the canvas box by dividing a target visible height by
// DAX_CANVAS_FILL, so Dax HIMSELF (not the box) hits that target.
const CAMERA_Z = 5
const CAMERA_FOV = 40
const TARGET_HEIGHT = 2.7
const FRUSTUM_HEIGHT = 2 * CAMERA_Z * Math.tan((CAMERA_FOV / 2) * (Math.PI / 180))
export const DAX_CANVAS_FILL = TARGET_HEIGHT / FRUSTUM_HEIGHT

export type DaxReaction = 'idle' | 'correct' | 'incorrect'

// Unique character animation clips in the export. Excludes the plane/path/util
// clips ("Plane.002Action.001", "BezierCircleAction", "KeyAction.001") and the
// exact duplicates ("Excited.001" == "Excited"; "Walking2"/"Walking2.001" ==
// "WalkCycle"). "Waving.001" is kept because it differs from "Waving".
export const DAX_CLIP_NAMES = [
  'Action', 'Excited', 'Nod', 'WalkCycle', 'WaveGesture', 'Waving', 'Waving.001'
]

// Friendly labels for the preview picker (the keys are the real clip names used
// to look the animation up). "Action" is the full sequence of every move.
export const DAX_CLIP_LABELS: Record<string, string> = {
  Action: 'All in one'
}

interface DaxAnimationProps {
  /** Bumps whenever we want Dax to react (e.g. an answer was submitted). */
  reactionKey?: number
  /** Which reaction to play. */
  reaction?: DaxReaction
  /** Force a specific animation clip by name (used by the preview picker). */
  overrideClip?: string
  /** Skip motion for users who prefer reduced motion. */
  reduceMotion?: boolean
  className?: string
}

/**
 * Applies a short procedural "reaction" burst (hop + spin for correct, shake for
 * incorrect) on top of whatever baked animation is playing. Optionally adds a
 * gentle idle bob (used by the placeholder, which has no baked idle clip).
 */
const useReactionMotion = (
  group: React.RefObject<THREE.Group>,
  reaction: DaxReaction,
  reactionKey: number,
  reduceMotion: boolean,
  idleBob: boolean
): void => {
  const burstRef = useRef<number>(0)

  // Re-fire on every reactionKey bump, even if the reaction type is unchanged.
  useEffect(() => {
    if (reaction !== 'idle') burstRef.current = 1
  }, [reaction, reactionKey])

  useFrame((state, delta) => {
    const grp = group.current
    if (grp === null) return

    if (reduceMotion) {
      grp.position.y = 0
      grp.rotation.set(0, 0, 0)
      grp.scale.setScalar(1)
      return
    }

    const t = state.clock.getElapsedTime()
    grp.position.y = idleBob ? Math.sin(t * 2) * 0.08 : 0
    grp.rotation.set(0, idleBob ? Math.sin(t * 0.8) * 0.25 : 0, 0)

    if (burstRef.current > 0) {
      burstRef.current = Math.max(0, burstRef.current - delta)
      const p = burstRef.current // 1 -> 0
      if (reaction === 'correct') {
        grp.position.y += Math.sin((1 - p) * Math.PI) * 0.6
        grp.rotation.y += (1 - p) * Math.PI * 2
        grp.scale.setScalar(1 + Math.sin((1 - p) * Math.PI) * 0.15)
      } else if (reaction === 'incorrect') {
        grp.rotation.z = Math.sin((1 - p) * Math.PI * 8) * 0.2
      }
    } else {
      grp.scale.setScalar(1)
    }
  })
}

// Clip preferences. The export names its clips descriptively; the plane/util
// clips ("Plane.002Action.001", "BezierCircleAction", "KeyAction.001", "Action")
// are ignored.
const IDLE_CLIPS = ['Waving', 'WaveGesture', 'Nod', 'WalkCycle']

/** The real exported Dax model. */
const DaxModel: React.FC<{ reaction: DaxReaction, reactionKey: number, reduceMotion: boolean, overrideClip?: string }> = ({ reaction, reactionKey, reduceMotion, overrideClip }) => {
  const group = useRef<THREE.Group>(null)
  const { scene, animations } = useGLTF(MODEL_URL)
  const names = useMemo(() => animations.map((c) => c.name), [animations])
  // Drive the clips with our own mixer (rather than drei's useAnimations) so
  // playback is deterministic and doesn't depend on when drei populates its
  // action map — which was leaving Dax frozen in the bind-pose T-pose.
  const mixer = useMemo(() => new THREE.AnimationMixer(scene), [scene])
  useFrame((_, delta) => mixer.update(delta))

  // Normalize export artifacts in the embedded materials: Skin/Pants/Sweater
  // ship with metallicFactor unset (glTF defaults to 1.0), so they render as
  // dark metal without an environment map; and Pants/Sweater carry a stray gray
  // emissive that washes their color. Force the character matte and drop the
  // emissive so the baked textures show their true (bright) colors.
  useEffect(() => {
    scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh
      if (!mesh.isMesh || /plane|floor/i.test(obj.name)) return
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
      mats.forEach((mat) => {
        const m = mat as THREE.MeshStandardMaterial
        m.metalness = 0
        m.emissive?.setRGB(0, 0, 0)
        m.needsUpdate = true
      })
    })
  }, [scene])

  // Remove the exported ground plane, then center + scale using the UNION of all
  // character sub-meshes. (GLTFLoader splits "Blippy" into one mesh per material,
  // so we can't rely on a single mesh. We also skip the plane/floor and any
  // helper geometry, and use each mesh's bind-pose geometry box, which is
  // reliable for skinned meshes.)
  const fitScale = useMemo(() => {
    scene.updateWorldMatrix(true, true)

    const toRemove: THREE.Object3D[] = []
    const box = new THREE.Box3()
    scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh
      if (!mesh.isMesh) return
      if (/plane|floor/i.test(obj.name)) {
        toRemove.push(obj)
        return
      }
      if (mesh.geometry.boundingBox === null) mesh.geometry.computeBoundingBox()
      const meshBox = mesh.geometry.boundingBox
      if (meshBox !== null) {
        box.union(meshBox.clone().applyMatrix4(mesh.matrixWorld))
      }
    })
    toRemove.forEach((obj) => obj.removeFromParent())

    if (box.isEmpty()) return 1
    const size = box.getSize(new THREE.Vector3())
    // Fit by HEIGHT (not the max dimension) so the arm span in a wave/idle pose
    // doesn't drive the scale. TARGET_HEIGHT (module constant) fills DAX_CANVAS_FILL
    // of the frustum, leaving margin for the live re-centering below.
    const height = size.y || 1
    return TARGET_HEIGHT / height
  }, [scene])

  // Live skeleton for per-frame re-centering. The animated pose sits differently
  // than the static bind pose, so centering on the bind box alone left Dax
  // dropping out of frame; instead we recenter on the actual bones each frame.
  const bones = useMemo(() => {
    let found: THREE.Bone[] = []
    scene.traverse((obj) => {
      const sk = obj as THREE.SkinnedMesh
      if (sk.isSkinnedMesh && found.length === 0) found = sk.skeleton.bones
    })
    return found
  }, [scene])

  const fitRef = useRef<THREE.Group>(null)
  const boneBox = useMemo(() => new THREE.Box3(), [])
  const tmp = useMemo(() => new THREE.Vector3(), [])
  useFrame(() => {
    const fit = fitRef.current
    const grp = group.current
    if (fit === null || grp === null || bones.length === 0) return
    boneBox.makeEmpty()
    for (const bone of bones) {
      bone.getWorldPosition(tmp)
      boneBox.expandByPoint(tmp)
    }
    boneBox.getCenter(tmp)
    grp.worldToLocal(tmp) // model center expressed in the (reaction) group's local space
    // One-step correction that keeps the model centered at the group origin,
    // independent of the reaction hop/spin applied to `grp`.
    fit.position.x -= tmp.x
    fit.position.y -= tmp.y
    fit.position.z -= tmp.z
  })

  // The idle clip to loop (or the override from the preview picker). The plane/
  // path/util clips are ignored so Dax never stands in the plane's T-pose.
  const desiredClip = useMemo(() => {
    if (overrideClip !== undefined && names.includes(overrideClip)) return overrideClip
    return IDLE_CLIPS.find((name) => names.includes(name)) ??
      names.find((n) => !/plane|bezier|key/i.test(n) && n !== 'Action') ?? null
  }, [names, overrideClip])

  // Base idle/override loop. Held in a ref so the reaction can fade it out/in.
  const idleActionRef = useRef<THREE.AnimationAction | null>(null)
  useEffect(() => {
    if (desiredClip === null) return
    const clip = animations.find((c) => c.name === desiredClip)
    if (clip === undefined) return
    const action = mixer.clipAction(clip)
    idleActionRef.current = action
    action.reset().setLoop(THREE.LoopRepeat, Infinity).fadeIn(0.3).play()
    return () => {
      action.fadeOut(0.2)
      action.stop()
      if (idleActionRef.current === action) idleActionRef.current = null
    }
  }, [mixer, animations, desiredClip])

  // On a correct answer (reactionKey bumps with reaction === 'correct'), play the
  // one-shot "Excited" clip, then fade back to the idle loop when it finishes.
  useEffect(() => {
    if (reaction !== 'correct' || reduceMotion || reactionKey === 0) return
    const clip = animations.find((c) => c.name === 'Excited')
    if (clip === undefined) return
    const idle = idleActionRef.current
    const excited = mixer.clipAction(clip)
    excited.reset().setLoop(THREE.LoopOnce, 1)
    excited.clampWhenFinished = false
    idle?.fadeOut(0.2)
    excited.fadeIn(0.2).play()

    // Return to idle after the clip's duration (LoopOnce doesn't loop back on its own).
    const timer = setTimeout(() => {
      excited.fadeOut(0.3)
      idle?.reset().fadeIn(0.3).play()
    }, clip.duration * 1000)

    return () => clearTimeout(timer)
  }, [mixer, animations, reaction, reactionKey, reduceMotion])

  return (
    <group ref={group}>
      <group ref={fitRef} scale={fitScale}>
        <primitive object={scene} />
      </group>
    </group>
  )
}

/**
 * Placeholder character (primitives) used if the GLB fails to load, so the
 * overlay never renders empty.
 */
const PlaceholderDax: React.FC<{ reaction: DaxReaction, reactionKey: number, reduceMotion: boolean }> = ({ reaction, reactionKey, reduceMotion }) => {
  const group = useRef<THREE.Group>(null)
  useReactionMotion(group, reaction, reactionKey, reduceMotion, true)

  return (
    <group ref={group}>
      <mesh position={[0, 0.1, 0]}>
        <capsuleGeometry args={[0.5, 0.7, 8, 16]} />
        <meshStandardMaterial color='#9FE1CB' />
      </mesh>
      <mesh position={[0, 1.05, 0]}>
        <sphereGeometry args={[0.45, 32, 32]} />
        <meshStandardMaterial color='#C0DD97' />
      </mesh>
      <mesh position={[0.18, 1.12, 0.38]}>
        <sphereGeometry args={[0.08, 16, 16]} />
        <meshStandardMaterial color='#3E4140' />
      </mesh>
      <mesh position={[-0.18, 1.12, 0.38]}>
        <sphereGeometry args={[0.08, 16, 16]} />
        <meshStandardMaterial color='#3E4140' />
      </mesh>
    </group>
  )
}

interface FallbackBoundaryProps {
  fallback: React.ReactNode
  children: React.ReactNode
}

/** Falls back to the placeholder if the GLB can't be loaded. */
class DaxErrorBoundary extends React.Component<FallbackBoundaryProps, { hasError: boolean }> {
  constructor (props: FallbackBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError (): { hasError: boolean } {
    return { hasError: true }
  }

  render (): React.ReactNode {
    return this.state.hasError ? this.props.fallback : this.props.children
  }
}

/**
 * Absolutely-positioned, non-interactive 3D overlay that renders Dax on top of
 * the standard (graphic/Unity) MCQ view. Gated by the caller via
 * `hasDaxAnimationFeatureFlag()`.
 */
const DaxAnimation: React.FC<DaxAnimationProps> = ({
  reactionKey = 0,
  reaction = 'idle',
  overrideClip,
  reduceMotion = false,
  className = ''
}: DaxAnimationProps) => {
  return (
    <div
      data-testid='dax-animation'
      aria-hidden='true'
      className={`pointer-events-none ${className}`}
    >
      <Canvas
        camera={{ position: [0, 0, CAMERA_Z], fov: CAMERA_FOV }}
        gl={{ alpha: true }}
        style={{ background: 'transparent' }}
      >
        <ambientLight intensity={0.7} />
        <hemisphereLight args={['#ffffff', '#8d8d8d', 1.0]} />
        <directionalLight position={[3, 5, 2]} intensity={1.6} />
        <directionalLight position={[-3, 2, -2]} intensity={0.5} />
        <Suspense fallback={null}>
          <DaxErrorBoundary
            fallback={<PlaceholderDax reaction={reaction} reactionKey={reactionKey} reduceMotion={reduceMotion} />}
          >
            <DaxModel reaction={reaction} reactionKey={reactionKey} reduceMotion={reduceMotion} overrideClip={overrideClip} />
          </DaxErrorBoundary>
        </Suspense>
      </Canvas>
    </div>
  )
}

export default DaxAnimation
